import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BaseUrl = process.env.REACT_APP_API_URL;

// Async thunk for applying advance leave
export const applyAdvanceLeave = createAsyncThunk(
    'LeaveForm/applyAdvanceLeave',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyAdvanceLeave`, payload);
            if (response.data.status === "Success" || response.data.success) {
                return response.data;
            } else {
                return rejectWithValue(response.data.message || "Failed to apply advance leave.");
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Error applying advance leave. Please try again.");
        }
    }
);

// Async thunk for applying half day leave
export const applyHalfDayLeave = createAsyncThunk(
    'leave/applyHalfDayLeave',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyHalfDayLeave`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to apply half day leave');
        }
    }
);

export const applyEarlyLeave = createAsyncThunk(
    'leave/applyEarlyLeave',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyEarlyLeave`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to apply early leave');
        }
    }
);

export const applyLateArrival = createAsyncThunk(
    'leave/applyLateArrival',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyLateArrival`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to apply late arrival');
        }
    }
);

export const applyTimeBreak = createAsyncThunk(
    'leave/applyTimeBreak',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyTimeBreak`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to apply time break');
        }
    }
);

export const applyEmergencyLeave = createAsyncThunk(
    'leave/applyEmergencyLeave',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/AdvanceLeave/ApplyEmergencyLeave`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to apply emergency leave');
        }
    }
);

// Async thunk for fetching all leaves history (Admin/Owner)
export const fetchAllLeavesHistory = createAsyncThunk(
    'LeaveForm/fetchAllLeavesHistory',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/AdvanceLeave/AllLeaves`);
            if (response.data.status === "Success" || response.data.success) {
                return response.data;
            } else {
                return rejectWithValue(response.data.message || "Failed to fetch all leaves history.");
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Error fetching leaves history.");
        }
    }
);

// Async thunk for fetching all advance leaves by user ID (approver inbox)
export const getAdvanceLeaves = createAsyncThunk(
    'LeaveForm/getAdvanceLeaves',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/AdvanceLeave/PManagerRequest/${userId}`);
            if (response.data.status === "find" || response.data.success) {
                return response.data;
            } else {
                return rejectWithValue(response.data.message || "Failed to get advance leaves.");
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Error fetching advance leaves.");
        }
    }
);

// Async thunk for updating leave status
export const updateLeaveStatus = createAsyncThunk(
    'LeaveForm/updateLeaveStatus',
    async ({ id, action, remark, name, approverId, position }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${BaseUrl}/AdvanceLeave/action/${id}`, { action, remark, name, approverId, position });
            if (response.data.status === "Success" || response.data.success) {
                return response.data;
            } else {
                return rejectWithValue(response.data.message || "Failed to update leave status.");
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || "Error updating leave status.");
        }
    }
);

const initialState = {
    loading: false,
    success: false,
    error: null,
    submitResult: null,
    listLoading: false,
    leaveData: [],
};

const LeaveFormSlice = createSlice({
    name: 'LeaveForm',
    initialState,
    reducers: {
        resetLeaveFormState: (state) => {
            state.loading = false;
            state.success = false;
            state.error = null;
            state.submitResult = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle applyAdvanceLeave
            .addCase(applyAdvanceLeave.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(applyAdvanceLeave.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.submitResult = action.payload;
                state.error = null;
            })
            .addCase(applyAdvanceLeave.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action.payload;
            })
            // Handle applyHalfDayLeave
            .addCase(applyHalfDayLeave.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(applyHalfDayLeave.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.submitResult = action.payload;
                state.error = null;
                state.leaveData.unshift(action.payload.data);
            })
            .addCase(applyHalfDayLeave.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action.payload;
            })
            // Handle applyEarlyLeave
            .addCase(applyEarlyLeave.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyEarlyLeave.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.leaveData.unshift(action.payload.data);
            })
            .addCase(applyEarlyLeave.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Handle applyLateArrival
            .addCase(applyLateArrival.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyLateArrival.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.leaveData.unshift(action.payload.data);
            })
            .addCase(applyLateArrival.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Handle applyTimeBreak
            .addCase(applyTimeBreak.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyTimeBreak.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.leaveData.unshift(action.payload.data);
            })
            .addCase(applyTimeBreak.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Handle applyEmergencyLeave
            .addCase(applyEmergencyLeave.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyEmergencyLeave.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.leaveData.unshift(action.payload.data);
            })
            .addCase(applyEmergencyLeave.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Handle getAdvanceLeaves
            .addCase(getAdvanceLeaves.pending, (state) => {
                state.listLoading = true;
                state.error = null;
            })
            .addCase(getAdvanceLeaves.fulfilled, (state, action) => {
                state.listLoading = false;
                state.leaveData = action.payload.All || [];
            })
            .addCase(getAdvanceLeaves.rejected, (state, action) => {
                state.listLoading = false;
                state.error = action.payload;
            })
            // Handle fetchAllLeavesHistory
            .addCase(fetchAllLeavesHistory.pending, (state) => {
                state.listLoading = true;
                state.error = null;
            })
            .addCase(fetchAllLeavesHistory.fulfilled, (state, action) => {
                state.listLoading = false;
                state.leaveData = action.payload.data || [];
            })
            .addCase(fetchAllLeavesHistory.rejected, (state, action) => {
                state.listLoading = false;
                state.error = action.payload;
            })
            // Handle updateLeaveStatus
            .addCase(updateLeaveStatus.fulfilled, (state, action) => {
                const updatedLeave = action.payload.data;
                const index = state.leaveData.findIndex(l => l._id === updatedLeave._id);
                if (index !== -1) {
                    state.leaveData[index] = updatedLeave;
                }
            });
    }
});

export const { resetLeaveFormState } = LeaveFormSlice.actions;

export default LeaveFormSlice.reducer;
